import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useState
} from 'react';
import { THEME } from '@utils/constants/app-constants';
import {
  colors as defaultColors,
  shadows,
  ShadowToken
} from '@utils/constants/style';
import { LocalStorageExtension as LocalStorage } from '@utils/extensions/storage.extension.ts';
import { useOrganization } from '@utils/api/users/use-get-organization.tsx';
import {
  getSafeColorForElement,
  getContrastTextColor,
  getBrandColorForBackground,
  darkenColor,
  lightenColor,
  hexToRgba
} from '@utils/helpers/color-utils';

export type Theme = 'light' | 'dark';
export type Elements = keyof typeof defaultColors;

interface ThemeColors {
  dark: string;
  light: string;
}

interface UserColors {
  [key: string]: ThemeColors | undefined;
}

interface Context {
  theme: Theme;
  toggleTheme: (mode: boolean) => void;
  getColour: (element: Elements) => string;
  setUserColors: (colors: UserColors) => void;
  brandColor?: string | null; // Original brand color from DB
  safeBrandColor?: string | null; // Auto-adjusted for visibility
  getBrandColorForBackground: (color: string) => string;
  getContrastTextColor: (backgroundColor: string) => string;
  darkenColor: (color: string, percent: number) => string;
  lightenColor: (color: string, percent: number) => string;
  getBrandTint: (alpha?: number) => string;
  getShadow: (token: ShadowToken) => string;
}

export const ThemeContext = createContext<Context | null>(null);

export default function ThemeProvider(props: PropsWithChildren) {
  const [theme, setTheme] = useState<Theme>('light');
  const [userColors, setUserColors] = useState<UserColors>({});
  const [brandColor, setBrandColor] = useState<string | null>(null); // Original from DB
  const [safeBrandColor, setSafeBrandColor] = useState<string | null>(null); // Adjusted for visibility
  const { data: organization } = useOrganization();

  // Retrieve theme from LocalStorage on initial render
  useEffect(() => {
    const savedTheme = LocalStorage.get(THEME) as Theme | null;
    if (savedTheme) {
      setTheme(savedTheme);
      // Apply the saved theme to DOM classes
      if (savedTheme === 'dark') {
        document.documentElement.classList.add('dark');
        document.body.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
        document.body.classList.remove('dark');
      }
    }
  }, []);

  useEffect(() => {
    const hasDark = () =>
      document.documentElement.classList.contains('dark') ||
      document.body.classList.contains('dark') ||
      !!document.getElementById('root')?.classList.contains('dark');

    const sync = () => setTheme(hasDark() ? 'dark' : 'light');

    sync();

    const observer = new MutationObserver(sync);
    // watch both <html> and <body> (and optionally root)
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });
    const root = document.getElementById('root');
    if (root)
      observer.observe(root, { attributes: true, attributeFilter: ['class'] });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (organization?.reseller_branding?.brand_color) {
      const originalColor = organization.reseller_branding.brand_color;
      setBrandColor(originalColor);

      // Always use the safe (adjusted) version for UI rendering
      // This ensures visibility while storing the original in the DB
      const safeColor = getBrandColorForBackground(originalColor, theme);
      setSafeBrandColor(safeColor);

      const customColor: UserColors = {
        activeText: {
          light: safeColor,
          dark: safeColor
        }
      };
      setUserColors(customColor);
    }
  }, [organization, theme]);

  const getColour = (element: Elements): string => {
    const userColor = userColors[element]?.[theme];

    if (userColor) {
      return getSafeColorForElement(userColor, theme);
    }

    return defaultColors[element][theme];
  };

  const toggleTheme = (mode: boolean) => {
    const newTheme: Theme = mode ? 'dark' : 'light';
    LocalStorage.set(THEME, newTheme);
    setTheme(newTheme);

    // Update DOM classes to match the theme
    if (newTheme === 'dark') {
      document.documentElement.classList.add('dark');
      document.body.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
      document.body.classList.remove('dark');
    }
  };

  return (
    <ThemeContext.Provider
      value={{
        theme,
        toggleTheme,
        getColour,
        setUserColors,
        brandColor, // Original color from DB
        safeBrandColor, // Auto-adjusted for visibility
        getBrandColorForBackground: (color: string) =>
          getBrandColorForBackground(color, theme),
        getContrastTextColor,
        darkenColor,
        lightenColor,
        getBrandTint: (alpha = 0.08) => {
          const base = safeBrandColor ?? defaultColors.activeText.light;
          return hexToRgba(base, alpha);
        },
        getShadow: (token: ShadowToken) => shadows[token][theme]
      }}
    >
      {props.children}
    </ThemeContext.Provider>
  );
}

export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeContext must be used within a ThemeProvider');
  }
  return context;
};
